import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Shield, Server, Lock, Users } from "lucide-react";
import { Footer } from "../components/Footer";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function DataStoragePage() {
  useEffect(() => {
    document.title = "Data Storage - Afro Fanthem";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <article className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8" data-testid="data-storage-headline">
            Data Storage
          </h1>

          <p className="text-gray-300 text-lg leading-relaxed mb-10">
            Afro Fanthem stores survey and registration information only for the purpose of supporting the project, improving the platform, and building aggregated insights about African football fans.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            <div className="bg-white/5 border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-xl text-white uppercase mb-3">Where Data Is Stored</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Survey and registration data is stored on secure cloud-hosted database servers. Data is encrypted in transit and at rest.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-xl text-white uppercase mb-3">Who Can Access It</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Access to collected data is limited to authorised project administrators. Afro Fanthem does not intend to sell individual fan responses.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-xl text-white uppercase mb-3">Security</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                We apply standard security practices including encrypted connections, access controls, and regular monitoring to protect stored data.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-xl text-white uppercase mb-3">Third-Party Tools</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Where third-party tools are used for surveys, forms, analytics, hosting, or communications, data may be processed through those services according to their own policies.
              </p>
            </div>
          </div>

          <div className="prose prose-invert prose-gray max-w-none space-y-6">
            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Retention</h2>
              <p className="text-gray-400 leading-relaxed">
                Data is retained for as long as it is needed to support the project, produce reports, and engage with clubs, leagues, and partners on behalf of African football fans. Specific retention periods may vary depending on the type of data and its purpose.
              </p>
              {/* Placeholder for legal/compliance specifics */}
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Questions</h2>
              <p className="text-gray-400 leading-relaxed">
                Users can contact Afro Fanthem to ask questions about how their data is stored or used.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4 text-[#FBBF24]" />
                  <a href="mailto:afrofanthem@afrofanthem.com" className="text-[#FBBF24] hover:underline">afrofanthem@afrofanthem.com</a>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4 text-[#FBBF24]" />
                  <a href="mailto:admin@satraders.co.uk" className="text-[#FBBF24] hover:underline">admin@satraders.co.uk</a>
                </li>
              </ul>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="inline-flex items-center gap-2 text-[#FBBF24] font-bold uppercase tracking-widest hover:gap-4 transition-all" data-testid="data-storage-back-link">
              <ArrowLeft className="w-5 h-5" />
              Back to Homepage
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
